// Rule Detail View - View and manage individual intelligence rules
import React, { useState, useEffect } from 'react';
import {
  Brain,
  ArrowLeft,
  Edit3,
  Play,
  Pause,
  Mail,
  AlertCircle,
  CheckCircle,
  Clock,
  Users,
  TrendingUp,
  Calendar
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

// Mock data - will be replaced with API calls
const mockRules = {
  '1': {
    id: '1',
    name: 'Intro Class Completers - No Follow Up',
    description: 'Students who completed "Intro to Sewing" but haven\'t enrolled in another class',
    category: 'Student Retention',
    status: 'active',
    condition: {
      type: 'class_completion',
      classTitle: 'Intro to Sewing',
      noFollowUpEnrollment: true,
      daysSinceCompletion: 7
    },
    action: {
      type: 'email',
      templateName: 'Follow-up Class Recommendations',
      autoSend: false
    },
    matchCount: 0,
    lastRun: null,
    createdAt: '2025-01-15',
    isActive: true,
    matches: [] // Students who match this rule
  }
};

const RuleDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading rule data
    setTimeout(() => {
      setRule(mockRules[id] || null);
      setLoading(false);
    }, 300);
  }, [id]);

  const handleToggleActive = () => {
    setRule({ ...rule, isActive: !rule.isActive });
  };

  const handleRunRule = () => {
    console.log('Running rule:', rule.id);
    // TODO: Implement rule execution
  };

  const getStatusBadge = () => {
    if (!rule.isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
          <Pause className="w-4 h-4" />
          Paused
        </span>
      );
    }

    if (rule.matchCount > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
          <AlertCircle className="w-4 h-4" />
          {rule.matchCount} Match{rule.matchCount !== 1 ? 'es' : ''}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
        <CheckCircle className="w-4 h-4" />
        Active
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A99D8]"></div>
      </div>
    );
  }

  if (!rule) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto text-center py-16">
          <AlertCircle className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Rule Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The intelligence rule you're looking for doesn't exist.
          </p>
          <button
            onClick={() => navigate('/intelligence')}
            className="bg-[#7A99D8] hover:bg-[#6A89C8] text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Back to Intelligence
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/intelligence')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Intelligence
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRunRule}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              <Play className="w-4 h-4" />
              Run Now
            </button>
            <button
              onClick={() => navigate(`/intelligence/edit/${rule.id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-[#7A99D8] hover:bg-[#6A89C8] text-white rounded-lg font-medium transition-colors"
            >
              <Edit3 className="w-4 h-4" />
              Edit Rule
            </button>
            <button
              onClick={handleToggleActive}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                rule.isActive
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                  : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
              }`}
            >
              {rule.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {rule.isActive ? 'Pause Rule' : 'Activate Rule'}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Brain className="w-8 h-8 text-[#7A99D8]" />
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{rule.name}</h1>
              {getStatusBadge()}
            </div>
            <p className="text-gray-600 dark:text-gray-400">{rule.description}</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Rule Details */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Rule Conditions</h2>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Condition Type</label>
                  <p className="text-gray-900 dark:text-white mt-1">Class Completion</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Target Class</label>
                  <p className="text-gray-900 dark:text-white mt-1">{rule.condition.classTitle}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Additional Criteria</label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-gray-900 dark:text-white">No follow-up enrollment</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-gray-900 dark:text-white">At least {rule.condition.daysSinceCompletion} days since completion</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Details */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Automated Action</h2>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1" />
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-1">Send Email</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Template: <span className="font-medium text-gray-900 dark:text-white">{rule.action.templateName}</span>
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      {rule.action.autoSend ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                          <span className="text-green-700 dark:text-green-300">Automatic sending enabled</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                          <span className="text-amber-700 dark:text-amber-300">Manual approval required</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Matches List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Current Matches ({rule.matchCount})
              </h2>

              {rule.matchCount === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-600 dark:text-gray-400">No students currently match this rule</p>
                </div>
              ) : (
                <div className="text-gray-600 dark:text-gray-400">
                  <p>Student matches will be displayed here when the rule is executed.</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Stats */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Statistics</h2>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Current Matches</span>
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">{rule.matchCount}</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <TrendingUp className="w-4 h-4" />
                    <span>Category</span>
                  </div>
                  <p className="text-gray-900 dark:text-white font-medium">{rule.category}</p>
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <Calendar className="w-4 h-4" />
                    <span>Created</span>
                  </div>
                  <p className="text-gray-900 dark:text-white font-medium">
                    {new Date(rule.createdAt).toLocaleDateString()}
                  </p>
                </div>

                {rule.lastRun && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <Clock className="w-4 h-4" />
                      <span>Last Run</span>
                    </div>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {new Date(rule.lastRun).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleDetail;
